import { FormattedMessage, FormattedNumber } from 'react-intl'

import { CurrencyStringCents } from '@b-stock/fe-core/bstock-react'
import {
  type EuMaxTaxExposureDto,
  EuMaxTaxExposureDtoTreatmentEnum,
} from '@b-stock/listing-api-client'

import { Card, Row, Label, Amount, Legend } from '../taxUi'

// Bid-confirmation EU VAT max-exposure render (FR-008). Three DTO
// states, same rules as the running-estimate render:
//
//   - `estimateUnavailable: true` → "unavailable" line (FR-020).
//   - `treatment: REVERSE_CHARGE` → the 0 amount rendered with the
//     legend (FR-021). Omitting the row is forbidden.
//   - `treatment: STANDARD` → max VAT amount + rate.
//
// Reading B (a prior in-flight proxy max bumping the committed amount
// above the current form values) is not called out separately here —
// the DTO's `committedBidAmount` already reflects the resolved figure,
// and MVP scope keeps the bid-confirmation banner simple. The Canadian
// counterpart's PriorBidAffordance can be added later if UX asks for it.

const MaxVatExposureDisplay = ({
  data,
  currencyCode,
}: {
  data: EuMaxTaxExposureDto
  currencyCode: string
}) => {
  if (data.estimateUnavailable) {
    return (
      <Card data-testid="max-vat-exposure-unavailable">
        <Label>
          <FormattedMessage id="BidConfirmationModal.maxVatExposure.unavailable" />
        </Label>
      </Card>
    )
  }

  const isReverseCharge =
    data.treatment === EuMaxTaxExposureDtoTreatmentEnum.ReverseCharge
  const rate = Number.parseFloat(data.rate ?? '0') || 0
  // Force 0 on REVERSE_CHARGE for defensive symmetry with
  // RunningVatEstimate — the DTO should already carry 0 here, but
  // a client-side clamp keeps FR-021 airtight against contract drift.
  const amountCents = isReverseCharge ? 0 : (data.maxVatAmount ?? 0)

  return (
    <Card data-testid="max-vat-exposure">
      <Row>
        <Label>
          <FormattedMessage
            id="BidConfirmationModal.maxVatExposure.label"
            values={{
              rate: (
                <FormattedNumber
                  value={rate}
                  style="percent"
                  maximumFractionDigits={3}
                />
              ),
            }}
          />
        </Label>
        <Amount data-testid="max-vat-exposure-amount">
          <CurrencyStringCents value={amountCents} currency={currencyCode} />
        </Amount>
      </Row>
      {isReverseCharge && data.treatmentLegend && (
        <Legend data-testid="max-vat-exposure-legend">
          {data.treatmentLegend}
        </Legend>
      )}
    </Card>
  )
}

export default MaxVatExposureDisplay
